import { Component, ViewChild, AfterViewInit, OnInit, Input } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { take } from 'rxjs/operators';
import { ApiService } from 'src/app/services/api.service';
import { AuthService } from 'src/app/services/auth.service';
import { DataService } from 'src/app/services/data.service';

export interface FeedRow {
  indexName: string;
  description: string;
  useCase: string[];
  qualifiedOperator: string[];
}

@Component({
  selector: 'app-indices-preview',
  templateUrl: './indices-preview.component.html',
  styleUrls: ['./indices-preview.component.scss']
})
export class IndicesPreviewComponent implements OnInit, AfterViewInit {
  displayedColumns: string[] = [
    'indexName',
    'description',
    'useCase',
    'qualifiedOperator'
  ];
  @Input() showBackToNewTask = false;

  dataSource = new MatTableDataSource<FeedRow>([]);

  @ViewChild(MatSort) sort!: MatSort;
  currentUser: any;
  industryTypes: any;
  tooltipImagePath: string;
  selectedIndustryList: any[] = [];
  indicesData: any[] = [];

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private router: Router,
    private localStorageService: DataService,
    
  ) {
    this.localStorageService.darkTheme.subscribe((newValue) => {
      this.tooltipImagePath = newValue
        ? '../../../assets/images/union-dark.svg'
        : '../../../assets/images/union.svg';
    });
  }

  ngOnInit(): void {
    this.authService.user.pipe(take(1)).subscribe((user) => {
      this.currentUser = user;
      // Load industry types and indices data
      this.loadIndicesData();
      this.loadIndustryTypes();
    });
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;

    this.dataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'indexName':
          return item.indexName;
        case 'description':
          return item.description;
        case 'useCase':
          return item.useCase ? item.useCase.join(', ') : '';
        case 'qualifiedOperator':
          return item.qualifiedOperator ? item.qualifiedOperator.join(', ') : '';
        default:
          return '';
      }
    };
  }

  private loadIndicesData(): void { 
    this.apiService
      .getAllInformationServices(this.currentUser?.id)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
            this.indicesData = response?.vadpServices;
        },
        error: (err) => {},
      });
  }

  // Load industry types from ApiService
  private loadIndustryTypes(): void {
    this.apiService.getIndustryTypes().pipe(take(1)).subscribe({
      next: (response) => {
        
        this.industryTypes = response.industriesList
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(item => ({
          ...item,
          selected: false
        }));
        
      },
      error: (err) => {},
    });
  }

  industryTypeChange(industry: any, index: number) {
    // Handle selected industry type change logic here
    if (industry.selected) {
      this.selectedIndustryList.push(industry?.name);
      // Load data based on selected industries
      this.loadFilteredData();
    } else {
      // Remove industry from selected list
      const index = this.selectedIndustryList.findIndex(
        (selectedItem) => selectedItem === industry.name
      );
      this.selectedIndustryList.splice(index, 1);
      this.selectedIndustryList.length === 0 ? this.dataSource.data = [] : this.loadFilteredData();
    };
    
  };


  loadFilteredData(): void {
    const filteredData = this.indicesData.filter((feed: any) => {

    // Industry filter
    const industryMatch =
      Array.isArray(feed.industries) &&
      feed.industries.some((industry: any) =>
        this.selectedIndustryList.includes(industry.name)
      );

    // Sensor suspension check
    const hasActiveSensor =
      Array.isArray(feed.sensors) &&
      feed.sensors.some((sensor: any) => sensor?.suspended === false);

      return industryMatch && hasActiveSensor;
    });

    this.dataSource.data = filteredData?.length
      ? this.mapToTableRows(filteredData)
      : [];

    this.applySort();
  }

  private mapToTableRows(data: any[]): FeedRow[] {
    return data.map((item) => {

      const operators: string[] = item.sensors
        .filter((sensor: any) => !sensor?.suspended)
        .map((sensor: any) => {
          const name: string = this.currentUser.obfuscated
            ? sensor.obfuscateName
            : sensor.sensor;

          return `${name} ${sensor.sensorType}`;
        });

      const uniqueOperators: string[] = [...new Set(operators)].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

      return {
        indexName: item.name,
        description: item.description,
        useCase: item.useCases || [],
        qualifiedOperator: uniqueOperators
      };
    });
  }

  private applySort(): void {
    if (this.sort) {
      this.dataSource.sort = this.sort;
    }
  }

  backToNewTask(): void {
    this.router.navigate(['/newtask']);
  }
  
}
